import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Table, Tag, Modal, Form, Input, InputNumber, Select, Switch,
  message, Popconfirm, Space, Typography, Empty, Avatar, Tooltip, Alert,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, TeamOutlined, WhatsAppOutlined, MailOutlined,
  TrophyOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { getActiveTeamId } from '../../utils/activeTeam';

const { Text, Title } = Typography;

const POSITION_OPTIONS = [
  { value: 'goleiro', label: 'Goleiro' },
  { value: 'zagueiro', label: 'Zagueiro' },
  { value: 'lateral', label: 'Lateral' },
  { value: 'volante', label: 'Volante' },
  { value: 'meia', label: 'Meia' },
  { value: 'atacante', label: 'Atacante' },
  { value: 'linha', label: 'Linha (qualquer)' },
];
const FOOT_OPTIONS = [
  { value: 'direito', label: 'Direito' },
  { value: 'esquerdo', label: 'Esquerdo' },
  { value: 'ambos', label: 'Ambos' },
];

export default function TeamRoster() {
  const teamId = getActiveTeamId();
  const [editing, setEditing] = useState(null);
  const [data, setData] = useState({ is_pro: false, items: [], customer_id: null });
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lookup, setLookup] = useState(null); // { exists, name, email_masked, already_in_team }
  const [form] = Form.useForm();

  // Lookup on-blur do telefone — se ja existe cp com esse phone, avisa
  // o organizador e pre-preenche nome. Email fica preservado no backend.
  const doLookup = useCallback(async (rawPhone) => {
    const clean = String(rawPhone || '').replace(/\D/g, '');
    if (clean.length !== 10 && clean.length !== 11) { setLookup(null); return; }
    try {
      const r = await api.get(`/community/teams/${teamId}/roster/lookup`, { params: { phone: clean } });
      setLookup(r.data);
      if (r.data.exists && r.data.name && !form.getFieldValue('name')) {
        form.setFieldsValue({ name: r.data.name });
      }
    } catch {
      // silencioso — lookup e nice-to-have
    }
  }, [teamId, form]);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const r = await api.get(`/community/teams/${teamId}/roster`);
      setData(r.data);
    } catch (e) {
      message.error(e.response?.data?.message || 'Erro ao carregar elenco');
    } finally { setLoading(false); }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setLookup(null);
    form.resetFields();
    form.setFieldsValue({ add_to_futezap: data.is_pro });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      name: row.name, phone: row.phone, email: row.email,
      position_pref: row.position_pref, level: row.level, foot: row.foot,
      altura_cm: row.altura_cm, peso_kg: row.peso_kg,
      jersey_number: row.jersey_number, notes: row.notes,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing?.cp_id) {
        // Editar: PUT sem phone (imutavel — pra "trocar" remove+add)
        const { phone, add_to_futezap, ...body } = values;
        await api.put(`/community/teams/${teamId}/roster/${editing.cp_id}`, body);
        message.success('Jogador atualizado');
      } else {
        await api.post(`/community/teams/${teamId}/roster`, values);
        message.success('Jogador adicionado ao elenco');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleImportFutezap = () => {
    Modal.confirm({
      title: 'Importar elenco do Futezap?',
      content: 'Vou trazer todos os jogadores ativos do time Futezap pro elenco da comunidade. Emails ficam em branco — jogadores completam depois no perfil, ou você pode editar aqui.',
      okText: 'Importar',
      onOk: async () => {
        try {
          const r = await api.post(`/community/teams/${teamId}/roster/import-futezap`);
          message.success(`Importados: ${r.data.linked} jogadores adicionados ao elenco (${r.data.created} novos na comunidade${r.data.skipped ? `, ${r.data.skipped} pulados por telefone inválido` : ''})`);
          load();
        } catch (e) {
          message.error(e.response?.data?.message || 'Erro ao importar');
        }
      },
    });
  };

  const handleRemove = async (item, alsoFutezap) => {
    try {
      const q = alsoFutezap ? '?also_futezap=1' : '';
      await api.delete(`/community/teams/${teamId}/roster/${item.cp_id}${q}`);
      message.success('Removido do elenco');
      load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Erro ao remover');
    }
  };

  const columns = [
    {
      title: 'Jogador', key: 'name',
      render: (_, r) => (
        <Space>
          <Avatar src={r.profile_photo} icon={<TeamOutlined />}
            style={{ background: r.profile_photo ? undefined : '#128C7E' }} />
          <div>
            <div style={{ fontWeight: 600 }}>{r.name}</div>
            {r.jersey_number && <Tag color="gold">#{r.jersey_number}</Tag>}
          </div>
        </Space>
      ),
    },
    {
      title: 'Contato', key: 'contact',
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          {r.phone && <div><WhatsAppOutlined /> {r.phone}</div>}
          {r.email
            ? <div><MailOutlined /> {r.email}</div>
            : r.cp_id && <div><Tag color="orange" style={{ fontSize: 10 }}>sem email</Tag></div>}
        </div>
      ),
    },
    {
      title: 'Posição / Nível', key: 'pos',
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          {r.position_pref && <Tag>{r.position_pref}</Tag>}
          {r.level && <Tag color="blue"><TrophyOutlined /> nvl {r.level}</Tag>}
          {r.foot && <Tag>{r.foot}</Tag>}
        </div>
      ),
    },
    {
      title: 'Vínculo', key: 'source', width: 130,
      render: (_, r) => {
        if (r.source === 'ambos') return <Tag color="green">Comunidade + Futezap</Tag>;
        if (r.source === 'comunidade') return <Tag color="cyan">Comunidade</Tag>;
        return <Tag color="orange">Só Futezap</Tag>;
      },
    },
    {
      title: '', key: 'actions', width: 140,
      render: (_, r) => r.cp_id ? (
        <Space size={4}>
          <Button size="small" onClick={() => openEdit(r)}>Editar</Button>
          <Popconfirm
            title={data.is_pro && r.futezap_player_id
              ? 'Remover só do elenco ou também do Futezap?'
              : 'Remover do elenco?'}
            okText={data.is_pro && r.futezap_player_id ? 'Só elenco' : 'Sim'}
            cancelText={data.is_pro && r.futezap_player_id ? 'Elenco + Futezap' : 'Não'}
            onConfirm={() => handleRemove(r, false)}
            onCancel={data.is_pro && r.futezap_player_id ? () => handleRemove(r, true) : undefined}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) : (
        <Tooltip title="Vem só de players do Futezap. Use 'Importar do Futezap' pra trazer pro elenco comunidade.">
          <Tag color="default">Só Futezap</Tag>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Elenco</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {data.is_pro
              ? 'Time Futezap Pro ativo — jogador cadastrado aqui pode entrar direto nas convocações'
              : 'Time só-comunidade — jogadores ficam no elenco pra amistosos/notícias'}
          </Text>
        </div>
        <Space>
          {data.is_pro && (
            <Button onClick={handleImportFutezap}>
              Importar do Futezap
            </Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Adicionar jogador
          </Button>
        </Space>
      </div>

      <Card size="small">
        <Table
          size="small"
          rowKey={r => r.cp_id || `fzp_${r.futezap_player_id}`}
          loading={loading}
          dataSource={data.items}
          columns={columns}
          pagination={{ pageSize: 30, showSizeChanger: false }}
          locale={{ emptyText: <Empty description="Sem elenco cadastrado ainda" /> }}
        />
      </Card>

      <Modal
        title={editing ? 'Editar jogador' : 'Novo jogador'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText={editing ? 'Salvar' : 'Cadastrar'}
        confirmLoading={saving}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Nome" rules={[{ required: true, min: 2 }]}>
            <Input placeholder="Nome do boleiro" />
          </Form.Item>
          <Form.Item name="phone" label="Whatsapp (DDD + número)"
            rules={editing ? [] : [{ required: true, pattern: /^\d{10,11}$/, message: '10 ou 11 dígitos, só números' }]}
            extra={editing ? 'Não editável (chave global). Pra trocar de telefone: remove e adiciona.' : 'Chave global do jogador na comunidade'}>
            <Input placeholder="11987654321" maxLength={11} disabled={!!editing}
                   onBlur={(e) => !editing && doLookup(e.target.value)} />
          </Form.Item>
          {!editing && lookup?.exists && (
            <Alert
              type={lookup.already_in_team ? 'warning' : 'info'}
              showIcon
              style={{ marginBottom: 12 }}
              message={
                lookup.already_in_team
                  ? 'Esse jogador já está no seu elenco'
                  : `${lookup.name} já tem cadastro na comunidade`
              }
              description={
                !lookup.already_in_team && (
                  lookup.has_email
                    ? <>Email vinculado: <strong>{lookup.email_masked}</strong>. Vamos preservar esse email — não precisa digitar de novo.</>
                    : 'Ainda não tem email cadastrado. Se preencher abaixo, vamos vincular.'
                )
              }
            />
          )}
          <Form.Item name="email" label="Email"
            rules={[
              // Se ja tem cp com email, email nao eh obrigatorio (backend preserva)
              { required: !(lookup?.exists && lookup?.has_email), type: 'email', message: 'Email válido — usamos pra notícias e jogos' },
            ]}
            extra={lookup?.exists && lookup?.has_email
              ? 'Opcional — se digitar, tentaremos substituir o email atual (só se não conflitar)'
              : 'Recebe notícias do time, agenda de jogos, convites'}>
            <Input placeholder="email@exemplo.com" />
          </Form.Item>
          <Space size={8} style={{ display: 'flex' }}>
            <Form.Item name="position_pref" label="Posição" style={{ flex: 1 }}>
              <Select options={POSITION_OPTIONS} allowClear placeholder="—" />
            </Form.Item>
            <Form.Item name="foot" label="Pé" style={{ width: 110 }}>
              <Select options={FOOT_OPTIONS} allowClear placeholder="—" />
            </Form.Item>
            <Form.Item name="level" label="Nível 1-10" style={{ width: 110 }}>
              <InputNumber min={1} max={10} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Space size={8} style={{ display: 'flex' }}>
            <Form.Item name="jersey_number" label="Camisa" style={{ width: 100 }}>
              <InputNumber min={1} max={99} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="altura_cm" label="Altura (cm)" style={{ width: 130 }}>
              <InputNumber min={100} max={230} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="peso_kg" label="Peso (kg)" style={{ width: 120 }}>
              <InputNumber min={30} max={200} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notas (opcional)">
            <Input.TextArea rows={2} placeholder="Observações internas do organizador" />
          </Form.Item>
          {data.is_pro && !editing && (
            <Form.Item name="add_to_futezap" label="Incluir nas convocações do Futezap"
              valuePropName="checked" tooltip="Cadastra o jogador em players do time (mensalista/avulso), passando a receber convocação">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
